import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Swords, DollarSign, Clock, CheckCircle, XCircle, 
  Gamepad2, Trophy, Users, Loader2, AlertTriangle, Play, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useConfirm } from '../components/CustomDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category display names
const CATEGORY_LABELS = {
  console_games: 'Console Games',
  pc_games: 'PC Games',
  mobile_games: 'Mobile Games',
  board_games: 'Board Games',
  sports: 'Sports',
  fitness: 'Fitness',
  reaction: 'Reaction',
  custom: 'Custom'
};

const MatchConfirm = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { showConfirm, ConfirmDialog } = useConfirm();
  const [match, setMatch] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [startingMatch, setStartingMatch] = useState(false);

  const fetchMatch = useCallback(async () => {
    try {
      const [matchRes, configRes] = await Promise.all([
        axios.get(`${API}/matches/${matchId}`, { withCredentials: true }),
        axios.get(`${API}/matches/config`, { withCredentials: true })
      ]);
      setMatch(matchRes.data);
      setConfig(configRes.data);
      
      // If match already started (active), redirect to match detail
      if (matchRes.data.status === 'active' || matchRes.data.status === 'in_progress') {
        navigate(`/match/${matchId}`);
      }
      // If match returned to open or was cancelled
      if (matchRes.data.status === 'open' || matchRes.data.status === 'cancelled') {
        toast.info('Match was cancelled or returned to lobby');
        navigate('/lobby');
      }
    } catch (error) {
      console.error('Error fetching match:', error);
      toast.error('Failed to load match');
      navigate('/lobby');
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate]);

  useEffect(() => {
    fetchMatch();
    // Poll for updates every 3 seconds
    const interval = setInterval(fetchMatch, 3000);
    return () => clearInterval(interval);
  }, [fetchMatch]);

  // Handle opponent accepting the match (Step 2 - joins the match)
  const handleAccept = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/matches/join/${matchId}`, {}, { withCredentials: true });
      toast.success('Challenge accepted! Waiting for creator to confirm...');
      refreshUser();
      setMatch(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept match');
    } finally {
      setConfirming(false);
    }
  };

  // Handle creator confirming and starting the match (Step 3 - final confirmation)
  const handleStartMatch = async () => {
    setStartingMatch(true);
    try {
      const res = await axios.post(`${API}/matches/confirm/${matchId}`, {}, { withCredentials: true });
      toast.success('Match started! Good luck!');
      refreshUser();
      
      // Navigate to match detail page
      navigate(`/match/${matchId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start match');
    } finally {
      setStartingMatch(false);
    }
  };

  const handleDecline = async () => {
    const confirmed = await showConfirm(
      'Are you sure you want to cancel this match? Your credits will be returned.',
      'Cancel Match'
    );
    if (!confirmed) return;
    
    setDeclining(true);
    try {
      await axios.post(`${API}/matches/decline/${matchId}`, {}, { withCredentials: true });
      toast.success('Match cancelled. Credits returned.');
      refreshUser();
      navigate('/lobby');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel match');
    } finally {
      setDeclining(false);
    }
  };

  if (loading || !match) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const isCreator = match.creator_id === user?.user_id;
  const isOpponent = match.opponent_id === user?.user_id;
  
  // 3-Step Match Flow:
  // Step 1: Creator creates match (status: 'open')
  // Step 2: Opponent accepts/joins (status: 'pending_acceptance', opponent_confirmed: true)
  // Step 3: Creator confirms to start (status: 'active')
  const isPendingAcceptance = match.status === 'pending_acceptance';
  const opponentHasAccepted = match.opponent_confirmed === true;
  const creatorNeedsToConfirm = isCreator && isPendingAcceptance && opponentHasAccepted;
  const opponentNeedsToAccept = isOpponent && !match.opponent_confirmed;
  
  const totalEntry = match.wager_amount * 2;
  const totalFee = match.platform_fee * 2;
  const winnerReward = totalEntry - totalFee;

  // Determine header text based on state
  const getHeaderText = () => {
    if (creatorNeedsToConfirm) {
      return { title: 'Confirm & Start Match', subtitle: 'Your opponent has accepted! Confirm to begin.' };
    }
    if (opponentNeedsToAccept) {
      return { title: 'Accept Challenge', subtitle: 'Review the match details and accept to compete.' };
    }
    return { title: 'Match Confirmation', subtitle: 'Waiting for confirmation...' };
  };

  const headerText = getHeaderText();

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4" data-testid="match-confirm-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className={`p-6 text-center ${
            creatorNeedsToConfirm 
              ? 'bg-gradient-to-r from-[#00FF94] to-[#00F0FF]' 
              : 'bg-gradient-to-r from-[#7000FF] to-[#00F0FF]'
          }`}>
            <Swords className="w-12 h-12 mx-auto mb-3 text-white" />
            <h1 className="font-heading text-2xl font-bold text-white">{headerText.title}</h1>
            <p className="text-white/70">{headerText.subtitle}</p>
          </div>

          {/* Match Details */}
          <div className="p-6 space-y-6">
            {/* Game Info with Category */}
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full">
                <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
                <span className="font-semibold">{match.game_name}</span>
              </div>
              {match.category && (
                <p className="text-white/50 text-sm">
                  Category: {CATEGORY_LABELS[match.category] || match.category}
                </p>
              )}
            </div>

            {/* Players - 3 Step Progress */}
            <div className="grid grid-cols-2 gap-4">
              {/* Player A (Creator) */}
              <div className={`p-4 rounded-xl border-2 ${
                isCreator 
                  ? 'border-[#00F0FF] bg-[#00F0FF]/5' 
                  : 'border-white/10 bg-white/5'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-white/50">CHALLENGER</span>
                  {isPendingAcceptance ? (
                    <Clock className="w-4 h-4 text-[#FFB800]" />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                  )}
                </div>
                <p className="font-semibold truncate">{match.creator_name}</p>
                {match.creator_elo && (
                  <p className="text-xs text-white/50">ELO: {match.creator_elo}</p>
                )}
                <p className={`text-xs mt-1 ${isPendingAcceptance ? 'text-[#FFB800]' : 'text-[#00FF94]'}`}>
                  {isPendingAcceptance ? 'Needs to confirm' : 'Created match'}
                </p>
                {isCreator && <span className="text-xs text-[#00F0FF]">(You)</span>}
              </div>

              {/* Player B (Opponent) */}
              <div className={`p-4 rounded-xl border-2 ${
                opponentHasAccepted 
                  ? 'border-[#00FF94] bg-[#00FF94]/5' 
                  : isOpponent
                    ? 'border-[#00F0FF] bg-[#00F0FF]/5'
                    : 'border-white/10 bg-white/5'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-white/50">OPPONENT</span>
                  {opponentHasAccepted ? (
                    <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                  ) : (
                    <Clock className="w-4 h-4 text-white/30" />
                  )}
                </div>
                <p className="font-semibold truncate">{match.opponent_name || 'Waiting...'}</p>
                {match.opponent_elo && (
                  <p className="text-xs text-white/50">ELO: {match.opponent_elo}</p>
                )}
                <p className={`text-xs mt-1 ${opponentHasAccepted ? 'text-[#00FF94]' : 'text-[#FFB800]'}`}>
                  {opponentHasAccepted ? 'Accepted!' : 'Waiting to accept...'}
                </p>
                {isOpponent && <span className="text-xs text-[#00F0FF]">(You)</span>}
              </div>
            </div>

            {/* Escrow Status Indicator */}
            {isPendingAcceptance && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/20">
                <Lock className="w-5 h-5 text-[#FFB800]" />
                <div>
                  <p className="text-[#FFB800] font-medium">Entry fees will be locked when match starts</p>
                  <p className="text-white/50 text-sm">Funds move to escrow after creator confirms</p>
                </div>
              </div>
            )}

            {/* Entry Fee Details */}
            <div className="bg-white/5 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/50">Your Entry</span>
                <span className="font-mono text-lg">${match.wager_amount?.toFixed(2)} each</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/50">Total Match Pool</span>
                <span className="font-mono">${(match.wager_amount * 2).toFixed(2)}</span>
              </div>
              <div className="border-t border-white/10 pt-3 flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#CCFF00]" />
                  Winner Receives
                </span>
                <span 
                  className="font-mono text-2xl font-bold text-[#CCFF00]"
                  style={{ textShadow: '0 0 15px rgba(204, 255, 0, 0.5)' }}
                >
                  ${match.prize_amount?.toFixed(2) || winnerReward.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Match Info */}
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white/50 text-sm">
                Both players enter the same match amount. The winner receives the payout after match confirmation.
              </p>
            </div>

            {/* Status Message - Creator needs to confirm */}
            {creatorNeedsToConfirm && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20">
                <Users className="w-5 h-5 text-[#00FF94]" />
                <div>
                  <p className="text-[#00FF94] font-medium">{match.opponent_name} has accepted your challenge!</p>
                  <p className="text-white/50 text-sm">Click "Start Match" to begin competing. Entry fees will be locked.</p>
                </div>
              </div>
            )}

            {/* Status Message - Opponent viewing before accepting */}
            {opponentNeedsToAccept && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/20">
                <Swords className="w-5 h-5 text-[#00F0FF]" />
                <div>
                  <p className="text-[#00F0FF] font-medium">{match.creator_name} challenged you!</p>
                  <p className="text-white/50 text-sm">Accept to join this match. Entry fee will be deducted.</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={handleDecline}
                disabled={declining || confirming || startingMatch}
                variant="outline"
                className="flex-1 border-[#FF2E2E] text-[#FF2E2E] hover:bg-[#FF2E2E]/10"
                data-testid="decline-match-btn"
              >
                {declining ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Cancel Match
              </Button>
              
              {/* Creator: Confirm & Start button */}
              {creatorNeedsToConfirm && (
                <Button
                  onClick={handleStartMatch}
                  disabled={startingMatch || declining}
                  className="flex-1 bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold"
                  data-testid="start-match-btn"
                >
                  {startingMatch ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Start Match
                </Button>
              )}

              {/* Opponent: Accept button */}
              {opponentNeedsToAccept && (
                <Button
                  onClick={handleAccept}
                  disabled={confirming || declining}
                  className="flex-1 bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
                  data-testid="accept-match-btn"
                >
                  {confirming ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Accept Challenge
                </Button>
              )}

              {/* Waiting state - already accepted, waiting for other party */}
              {!creatorNeedsToConfirm && !opponentNeedsToAccept && (
                <Button
                  disabled
                  className="flex-1 bg-white/10 text-white/50"
                  data-testid="waiting-btn"
                >
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Waiting...
                </Button>
              )}
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5 text-xs text-white/50">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                By accepting, you agree to compete in this skill-based match. If you cancel after both players accept, 
                you may forfeit your entry fee. Timer starts when the match begins.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      <ConfirmDialog />
    </DashboardLayout>
  );
};

export default MatchConfirm;
